// The chat's two voices (D82): the user's message on the right with its ticks (one when sent,
// two when the worker has it), the agent's reply on the left as Markdown in the green of a run's
// answer (S137).
import { Check, Checks, Robot, WarningCircle } from "@phosphor-icons/react";

import { useT } from "../i18n";
import { cn } from "../ui/cn";
import { Markdown } from "../ui/markdown/Markdown";
import type { PendingPrompt } from "./model";

export type Delivery = PendingPrompt["state"];

export function UserMessage({ text, delivery, error }: { text: string; delivery: Delivery; error?: string }) {
  const t = useT().chat;
  const label =
    delivery === "delivered"
      ? t.turn.delivered
      : delivery === "sent"
        ? t.turn.sent
        : delivery === "failed"
          ? t.turn.sendFailed(error ?? "")
          : delivery === "waiting"
            ? t.turn.waitingWorker
            : t.turn.sending;
  return (
    <div className="flex justify-end" data-testid="chat-user" data-delivery={delivery}>
      <div
        className={cn(
          "max-w-[85%] whitespace-pre-wrap break-words rounded-2xl rounded-ee-md px-4 py-3 text-[13.5px] leading-relaxed",
          delivery === "failed" ? "border border-red/40 bg-red-lighter/40 text-gray-900" : "bg-primary text-primary-foreground",
        )}
      >
        {text}
        <div className="mt-1.5 flex items-center justify-end gap-1 text-[11px] opacity-75">
          {t.you} · {label}
          {delivery === "delivered" ? <Checks weight="bold" className="h-3.5 w-3.5" aria-hidden /> : null}
          {delivery === "sent" ? <Check weight="bold" className="h-3.5 w-3.5" aria-hidden /> : null}
          {delivery === "failed" ? <WarningCircle weight="fill" className="h-3.5 w-3.5" aria-hidden /> : null}
        </div>
      </div>
    </div>
  );
}

export function AgentMessage({ text, footer }: { text: string; footer?: string }) {
  const t = useT().chat;
  return (
    <div className="flex gap-3" data-testid="chat-agent">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-900 text-gray-0">
        <Robot weight="duotone" className="h-[18px] w-[18px]" />
      </span>
      <div className="min-w-0 max-w-[85%] rounded-2xl rounded-ss-md border border-green/30 bg-green-lighter/50 px-4 py-3 text-[13.5px] leading-relaxed text-gray-800">
        <Markdown source={text} />
        <div className="mt-1.5 text-[11px] text-gray-500">{footer ?? t.agent}</div>
      </div>
    </div>
  );
}
